import { router } from "@inertiajs/react";
import { LoaderIcon, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
	type ExternalProp,
	GLOBAL_MODES,
	type GlobalMode,
	type LocationMode,
	type RoutingEnvSettings,
	type RoutingSettings,
} from "@/types/admin-portal/routing-mode";
import { formatWib, sendRouting } from "./api";
import { LocationModeSelect, ReasonField } from "./fields";
import { LoadError } from "./load-error";

interface GlobalModeConfigProps {
	settings?: ExternalProp<RoutingSettings>;
	updateUrl: string;
}

// One status line per mode: what the global switch means for the location list below.
const STATUS_DOT: Record<GlobalMode, string> = {
	PER_LOCATION: "bg-emerald-500",
	ALL_HERE: "bg-muted-foreground",
	ALL_HAVERSINE: "bg-amber-500",
};

/**
 * The global switch as a segmented control above the locations table. It shows the
 * backend's own env; picking another mode opens a confirmation that writes every env.
 */
export function GlobalModeConfig({
	settings,
	updateUrl,
}: GlobalModeConfigProps) {
	const { t } = useTranslation("routing-mode");
	const [editing, setEditing] = useState<{
		mode: GlobalMode;
		forceSave: boolean;
	} | null>(null);

	if (settings?.error || !settings?.data) {
		return (
			<LoadError
				error={settings?.error ?? t("failed.body")}
				only={["settings"]}
			/>
		);
	}

	const { currentEnv, hereApiEnabled, envs } = settings.data;
	const current = envs.find((e) => e.env === currentEnv) ?? envs[0];
	if (!current) return null;

	// no_location_mode is only read under PER_LOCATION; the other modes ignore it.
	const perLocation = current.mode === "PER_LOCATION";
	const differing = envs.filter(
		(e) =>
			e.mode !== current.mode ||
			(perLocation && e.noLocationMode !== current.noLocationMode),
	);

	return (
		<section
			aria-labelledby="routing-global-mode"
			className="flex flex-col gap-3 rounded-lg border bg-card p-4"
		>
			<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
				<h2 id="routing-global-mode" className="text-sm font-semibold">
					{t("global.title")}
				</h2>
				<fieldset
					aria-labelledby="routing-global-mode"
					className="flex min-w-0 flex-col gap-0.5 rounded-md border bg-muted p-0.5 sm:flex-row"
				>
					{GLOBAL_MODES.map((m) => {
						const active = m === current.mode;
						return (
							<button
								key={m}
								type="button"
								aria-pressed={active}
								onClick={() =>
									!active && setEditing({ mode: m, forceSave: false })
								}
								className={cn(
									"whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-colors",
									"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
									active
										? "cursor-default bg-background text-foreground shadow-sm"
										: "text-muted-foreground hover:bg-background/60 hover:text-foreground active:bg-background",
								)}
							>
								{t(`global.modes.${m}.label`)}
							</button>
						);
					})}
				</fieldset>
			</div>

			<p className="flex items-start gap-2 text-sm">
				<span
					aria-hidden
					className={cn(
						"mt-1.5 size-2 shrink-0 rounded-full",
						STATUS_DOT[current.mode],
					)}
				/>
				<span>
					<span className="font-medium">
						{t(`global.status.${current.mode}.title`)}
					</span>{" "}
					<span className="text-muted-foreground">
						{t(`global.status.${current.mode}.body`)}
					</span>
				</span>
			</p>

			<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
				{perLocation && (
					<span className="flex items-center gap-1.5">
						{t("global.no_location_mode")}:
						<span className="font-medium text-foreground">
							{current.noLocationMode}
						</span>
						{!current.noLocationModeStored && (
							<Badge variant="outline">{t("default_badge")}</Badge>
						)}
						<Button
							variant="link"
							size="sm"
							className="h-auto p-0 text-xs"
							onClick={() =>
								setEditing({ mode: current.mode, forceSave: false })
							}
						>
							{t("global.edit")}
						</Button>
					</span>
				)}
				<span className="flex items-center gap-1.5">
					{t("global.updated_at", { at: formatWib(current.updatedAt) })}
					{!current.modeStored && (
						<Badge variant="outline">{t("default_badge")}</Badge>
					)}
				</span>
			</div>

			{differing.length > 0 && (
				<p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
					{t("global.envs_differ", {
						envs: differing
							.map(
								(e) =>
									`${e.env}: ${t(`global.modes.${e.mode}.label`)}${perLocation ? ` / ${e.noLocationMode}` : ""}`,
							)
							.join(", "),
					})}
					<Button
						variant="link"
						size="sm"
						className="h-auto p-0 text-xs"
						onClick={() => setEditing({ mode: current.mode, forceSave: true })}
					>
						{t("global.align")}
					</Button>
				</p>
			)}

			{!hereApiEnabled && (
				<Alert variant="destructive">
					<TriangleAlert className="size-4" />
					<AlertTitle>{t("global.here_disabled.title")}</AlertTitle>
					<AlertDescription>{t("global.here_disabled.body")}</AlertDescription>
				</Alert>
			)}

			{editing && (
				<EditSettingsDialog
					current={current}
					initialMode={editing.mode}
					forceSave={editing.forceSave}
					updateUrl={updateUrl}
					onClose={() => setEditing(null)}
				/>
			)}
		</section>
	);
}

function EditSettingsDialog({
	current,
	initialMode,
	forceSave,
	updateUrl,
	onClose,
}: {
	current: RoutingEnvSettings;
	/** the segment the admin clicked */
	initialMode: GlobalMode;
	/** the envs disagree, so saving unchanged values still aligns them */
	forceSave: boolean;
	updateUrl: string;
	onClose: () => void;
}) {
	const { t } = useTranslation("routing-mode");
	const [mode, setMode] = useState<GlobalMode>(initialMode);
	const [noLocationMode, setNoLocationMode] = useState<LocationMode>(
		current.noLocationMode,
	);
	const [reason, setReason] = useState("");
	const [saving, setSaving] = useState(false);
	// After a failure part-way, the reload shows the envs already written as current,
	// so nothing reads as changed; saving again must still reach the env that failed.
	const [failedPartWay, setFailedPartWay] = useState(false);

	const modeChanged = mode !== current.mode;
	// Only PER_LOCATION reads no_location_mode, so it is neither shown nor sent otherwise.
	const perLocation = mode === "PER_LOCATION";
	const noLocationChanged =
		perLocation && noLocationMode !== current.noLocationMode;
	const canSave =
		(modeChanged || noLocationChanged || forceSave || failedPartWay) && !saving;

	async function onSave() {
		setSaving(true);
		// The full state goes to every env, so envs that drifted apart end up equal.
		const result = await sendRouting<{ changed: number }>(
			updateUrl,
			"PUT",
			{
				mode,
				no_location_mode: perLocation ? noLocationMode : undefined,
				reason: reason.trim() || undefined,
			},
			t("messages.error"),
		);
		setSaving(false);
		// Also after a failure: it stops part-way, so some envs (the current one
		// included) may be written already.
		router.reload({ only: ["settings", "locations"] });

		if (!result.ok) {
			toast.error(result.error, { duration: Number.POSITIVE_INFINITY });
			setFailedPartWay(true);
			return;
		}
		toast.success(
			result.data.changed
				? t("messages.saved_propagation")
				: t("messages.no_change"),
		);
		onClose();
	}

	return (
		<Dialog open onOpenChange={(open) => !open && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t("global.dialog.title")}</DialogTitle>
					<DialogDescription>
						{t("global.dialog.description")}
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-2">
						<Label>{t("global.mode")}</Label>
						<Select
							value={mode}
							onValueChange={(v) => setMode(v as GlobalMode)}
						>
							<SelectTrigger className="bg-input shadow-inner">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{GLOBAL_MODES.map((m) => (
									<SelectItem key={m} value={m}>
										{t(`global.modes.${m}.label`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="text-xs text-muted-foreground">
							{t(`global.modes.${mode}.hint`)}
						</p>
					</div>

					{perLocation && (
						<div className="flex flex-col gap-2">
							<Label>{t("global.no_location_mode")}</Label>
							<LocationModeSelect
								value={noLocationMode}
								onChange={setNoLocationMode}
							/>
							<p className="text-xs text-muted-foreground">
								{t("global.no_location_hint")}
							</p>
						</div>
					)}

					<ReasonField
						id="routing-reason"
						value={reason}
						onChange={setReason}
					/>

					{(modeChanged || noLocationChanged) && (
						<div className="rounded-md border bg-muted/40 p-3 text-sm">
							<p className="font-medium">{t("global.dialog.summary")}</p>
							<ul className="mt-1 list-disc pl-5">
								{modeChanged && (
									<li>
										{t("global.mode")}:{" "}
										{t(`global.modes.${current.mode}.label`)} →{" "}
										{t(`global.modes.${mode}.label`)}
									</li>
								)}
								{noLocationChanged && (
									<li>
										{t("global.no_location_mode")}: {current.noLocationMode} →{" "}
										{noLocationMode}
									</li>
								)}
							</ul>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={saving}>
						{t("actions.cancel")}
					</Button>
					<Button onClick={onSave} disabled={!canSave}>
						{saving && <LoaderIcon className="size-4 animate-spin" />}
						{t("actions.save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
